<div
  class="flex flex-col gap-8 h-full overflow-y-auto custom-scrollbar min-h-[450px] p-6 pb-12"
>
  <!-- Header -->
  <div class="flex items-center justify-between mb-sm">
    <div class="flex items-center gap-3">
      <h2 class="text-xl font-bold leading-normal text-dark-text">
        {{ 'widgetMenu' | translate }}
      </h2>
      <span
        class="text-xs px-1.5 py-0.5 rounded-md text-gray-text bg-bg-soft border border-line"
      >
        {{ widgetShortcut }}
      </span>
    </div>
    <button
      (click)="close()"
      tabindex="-1"
      class="p-2 rounded-xl transition-colors cursor-pointer text-gray-text hover:bg-bg-soft"
    >
      <ic-close [width]="14" [height]="14"></ic-close>
    </button>
  </div>

  <!-- Active Widgets -->
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h3 class="text-[10px] font-bold uppercase tracking-widest text-gray-text/60">
        {{ 'activeWidgets' | translate }} ({{ widgetService.activeWidgets().length }}/10)
      </h3>

      <div class="relative">
        <button
          #addButton
          type="button"
          (click)="addWidgetPopover.toggle($event)"
          class="w-9 h-9 flex items-center justify-center rounded-md hover:scale-105 transition-all cursor-pointer shadow-card bg-white"
          [matTooltip]="'addNewWidget' | translate"
        >
          <ic-plus
            startColor="var(--color-primary-start-color)"
            endColor="var(--color-primary-end-color)"
            [width]="16"
            [height]="16"
          ></ic-plus>
        </button>
        <app-popover
          #addWidgetPopover
          direction="left"
          styleClass="manual-popover premium-widget-menu"
          [appendTo]="'body'"
          [baseZIndex]="11000"
          [showArrow]="false"
        >
          <div
            class="flex flex-col min-w-[240px] max-h-[50dvh] overflow-y-auto custom-scrollbar p-xs gap-xs"
          >
            @for (item of widgetMenuItems; track $index) {
              @if (item.separator) {
                <div class="h-px bg-line my-xs mx-sm shrink-0"></div>
              } @else {
                <button
                  type="button"
                  class="popover-menu-item group w-full text-left text-sm font-semibold text-dark-text px-sm py-xs rounded-md shrink-0"
                  (click)="onAddMenuItemClick(item); addWidgetPopover.hide()"
                >
                  <span>{{ item.label }}</span>
                </button>
              }
            }
          </div>
        </app-popover>
      </div>
    </div>

    <div
      class="grid grid-cols-6 gap-3"
      cdkDropList
      cdkDropListOrientation="horizontal"
      (cdkDropListDropped)="onDrop($event)"
    >
      @for(widget of widgetService.activeWidgets(); track widget.id) {
      <div
        cdkDrag
        [cdkDragDisabled]="widget.fixed"
        tabindex="0"
        (click)="navigateToWidget(widget)"
        (keydown.enter)="navigateToWidget(widget)"
        class="widget-focus-ring group relative flex flex-col justify-center p-3 bg-white rounded-lg shadow-card transition-all duration-300 cursor-pointer active:scale-95 min-h-[72px] hover:-translate-y-1"
      >
        <div class="flex items-center gap-2 w-full">
          <div
            class="w-7 h-7 shrink-0 flex items-center justify-center rounded-xl transition-colors"
          >
            @if(getIcon(widget)) {
            <ng-container
              *ngComponentOutlet="
                getIcon(widget);
                inputs: {
                  width: 13,
                  height: 13,
                  startColor: 'var(--color-primary-start-color)',
                  endColor: 'var(--color-primary-end-color)'
                }
              "
            ></ng-container>
            }
          </div>
          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span
              class="text-xs font-bold mb-prop leading-tight truncate transition-colors text-dark-text group-hover:text-primary"
            >
              {{
                (widget.type === 'item' && widget.label.includes(':')
                  ? widget.label.split(':')[0]
                  : widget.label) | translate
              }}
            </span>
            <span class="text-[9px] uppercase font-bold text-gray-text/40 tracking-tight">
              {{ widget.itemType || '' | translate }}
            </span>
          </div>
        </div>

        @if(!widget.fixed) {
        <button
          (click)="
            $event.stopPropagation(); widgetService.removeWidget(widget.id)
          "
          class="absolute -top-1.5 -right-1.5 opacity-0 group-hover:opacity-100 p-1 bg-white border border-line shadow-sm hover:bg-red-50 rounded-full transition-all duration-200 cursor-pointer z-10"
          [matTooltip]="'remove' | translate"
        >
          <ic-close
            startColor="var(--color-danger)"
            [width]="8"
            [height]="8"
          ></ic-close>
        </button>
        }

        <div
          *cdkDragPlaceholder
          class="border-2 border-dashed border-line rounded-2xl min-h-[72px] bg-bg-soft"
        ></div>
      </div>
      } @empty {
      <div
        class="col-span-6 flex items-center justify-center border-2 border-dashed border-line rounded-2xl h-20 bg-bg-soft"
      >
        <p class="text-xs text-gray-text">
          {{ 'addWidgetsToCustomizeYourView' | translate }}
        </p>
      </div>
      }
    </div>
  </section>

  <!-- Last Viewed -->
  <section class="flex flex-col gap-4">
    <h3 class="text-[10px] font-bold uppercase tracking-widest text-gray-text/60">
      {{ 'lastViewed' | translate }}
    </h3>
    <div class="flex flex-wrap gap-4">
      @if (widgetService.lastViewedShipment(); as shipment) {
      <a
        [routerLink]="['/shipments/details', shipment.id, 'info']"
        (click)="close()"
        class="widget-focus-ring group relative flex flex-col justify-center p-3 bg-white rounded-lg shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-1 min-h-[72px] flex-1 min-w-[180px]"
      >
        <div class="flex items-center gap-2 w-full">
          <div class="w-7 h-7 shrink-0 flex items-center justify-center rounded-xl transition-colors">
            <ic-plane [width]="13" [height]="13" startColor="var(--color-primary-start-color)"></ic-plane>
          </div>
          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span class="text-xs font-bold truncate transition-colors text-dark-text group-hover:text-primary">{{ shipment.shipmentNo }}</span>
            <span class="text-[9px] uppercase font-bold text-gray-text/40 tracking-tight">
              {{ 'shipments' | translate }}
            </span>
          </div>
        </div>
      </a>
      }

      @if (widgetService.lastViewedDeclaration(); as declaration) {
      <a
        [routerLink]="['/declarations/details', declaration.id, 'info']"
        (click)="close()"
        class="widget-focus-ring group relative flex flex-col justify-center p-3 bg-white rounded-lg shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-1 min-h-[72px] flex-1 min-w-[180px]"
      >
        <div class="flex items-center gap-2 w-full">
          <div class="w-7 h-7 shrink-0 flex items-center justify-center rounded-xl transition-colors">
            <ic-parcelbox [width]="13" [height]="13" startColor="var(--color-primary-start-color)"></ic-parcelbox>
          </div>
          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span class="text-xs font-bold truncate transition-colors text-dark-text group-hover:text-primary">{{ declaration.lrn || declaration.declarationNo }}</span>
            <span class="text-[9px] uppercase font-bold text-gray-text/40 tracking-tight">
              {{ 'declarations' | translate }}
            </span>
          </div>
        </div>
      </a>
      }

      @if (!isBroker && !isClient && widgetService.lastViewedBroker(); as broker) {
      <a
        [routerLink]="['/settings/brokers/details', broker.id, 'info']"
        (click)="close()"
        class="widget-focus-ring group relative flex flex-col justify-center p-3 bg-white rounded-lg shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-1 min-h-[72px] flex-1 min-w-[180px]"
      >
        <div class="flex items-center gap-2 w-full">
          <div class="w-7 h-7 shrink-0 flex items-center justify-center rounded-xl transition-colors">
            <ic-broker-solid [width]="13" [height]="13" startColor="var(--color-primary-start-color)"></ic-broker-solid>
          </div>

          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span class="text-xs font-bold truncate transition-colors text-dark-text group-hover:text-primary">{{ broker.name }}</span>
            <span class="text-[9px] uppercase font-bold text-gray-text/40 tracking-tight">
              {{ 'brokers' | translate }}
            </span>
          </div>
        </div>
      </a>
      }

      @if (!isClient && widgetService.lastViewedClient(); as client) {
      <a
        [routerLink]="['/settings/clients/details', client.id, 'info']"
        (click)="close()"
        class="widget-focus-ring group relative flex flex-col justify-center p-3 bg-white rounded-lg shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-1 min-h-[72px] flex-1 min-w-[180px]"
      >
        <div class="flex items-center gap-2 w-full">
          <div class="w-7 h-7 shrink-0 flex items-center justify-center rounded-xl transition-colors">
            <ic-client-solid [width]="13" [height]="13" startColor="var(--color-primary-start-color)"></ic-client-solid>
          </div>

          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span class="text-xs font-bold truncate transition-colors text-dark-text group-hover:text-primary">{{ client.name }}</span>
            <span class="text-[9px] uppercase font-bold text-gray-text/40 tracking-tight">
              {{ 'clients' | translate }}
            </span>
          </div>
        </div>
      </a>
      }

      @if (!widgetService.lastViewedShipment() && !widgetService.lastViewedDeclaration() && ((isBroker || isClient) || !widgetService.lastViewedBroker()) && (isClient || !widgetService.lastViewedClient())) {
        <div class="flex-1 flex items-center justify-center py-8 border-2 border-dashed border-line rounded-2xl bg-bg-soft/50">
          <span class="text-xs text-gray-text/60 italic">{{ 'noRecentItems' | translate }}</span>
        </div>
      }
    </div>
  </section>

  <!-- Jump Back In -->
  <section class="flex flex-col gap-4">
    <h3 class="text-[10px] font-bold uppercase tracking-widest text-gray-text/60">
      {{ 'jumpBackIn' | translate }}
    </h3>
    <div class="flex flex-wrap gap-4">
      <!-- Dashboard Link -->
      <a
        #homeLink
        tabindex="0"
        autofocus
        routerLink="/dashboard/shipments"
        (click)="close()"
        class="widget-focus-ring group flex items-center gap-4 px-6 py-3 bg-white rounded-xl shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
      >
        <div
          class="w-10 h-10 flex items-center justify-center rounded-2xl bg-bg-soft transition-colors"
        >
          <ic-home
            [width]="16"
            [height]="16"
            startColor="var(--color-primary-start-color)"
            endColor="var(--color-primary-end-color)"
          ></ic-home>
        </div>
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-bold text-dark-text">{{
            'dashboard' | translate
          }}</span>
          <span class="text-[10px] text-gray-text/60 font-medium uppercase tracking-tight">{{ 'home' | translate }}</span>
        </div>
        <ic-chevron-right
          startColor="var(--color-light-text)"
          [width]="10"
          [height]="10"
          class="shrink-0 ml-1"
        ></ic-chevron-right>
      </a>

      @for(history of widgetService.routeHistory().slice().reverse(); track
      history.url; let last = $last) {
      <a
        [routerLink]="history.url"
        (click)="close()"
        class="widget-focus-ring group flex items-center gap-4 px-6 py-3 bg-white rounded-xl shadow-card transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
        [ngClass]="
          last
            ? 'border-2 border-primary/20 bg-primary-overlay ring-1 ring-primary/5'
            : ''
        "
      >
        <div
          class="w-10 h-10 flex items-center justify-center rounded-2xl bg-bg-soft transition-colors"
        >
          <ic-compass
            [width]="16"
            [height]="16"
            startColor="var(--color-gray-text)"
          ></ic-compass>
        </div>
        <div class="flex flex-col min-w-0">
          <span
            class="text-sm font-bold truncate max-w-[140px] text-dark-text"
            >{{ history.label | translate }}</span>
          <span class="text-[10px] truncate max-w-[140px] text-gray-text/50 font-medium uppercase tracking-tight">
            {{ (history.url.split('/')[1] || '') | translate }}
          </span>
        </div>
        <ic-chevron-right
          startColor="var(--color-light-text)"
          [width]="10"
          [height]="10"
          class="shrink-0 ml-1"
        ></ic-chevron-right>
      </a>
      }
    </div>
  </section>
</div>
